import { lazy } from "react";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Bounded cold reloads per tab: WebKit omits the URL from dynamic-import
// errors, so the only way to discard the module-map failure cache is reloading
// the document. Count (not boolean) so a later chunk can still recover once.
const RETRY_RELOAD_KEY = "lazyWithRetry:reloads";
const MAX_RELOADS = 2;

const readReloads = () => {
	try {
		return Number(sessionStorage.getItem(RETRY_RELOAD_KEY) ?? 0) || 0;
	} catch {
		return 0;
	}
};

// Any successful import proves the tab is healthy again: replenish the
// reload budget, or one incident permanently disables recovery
const clearReloads = () => {
	try {
		sessionStorage.removeItem(RETRY_RELOAD_KEY);
	} catch {
		// storage unavailable (private mode): nothing to replenish
	}
};

const coldReload = (error) => {
	try {
		const reloads = readReloads();
		if (reloads < MAX_RELOADS) {
			sessionStorage.setItem(RETRY_RELOAD_KEY, String(reloads + 1));
			// Read-back: some environments silently no-op setItem, which
			// would loop reloads forever (counter never increments)
			if (readReloads() !== reloads + 1) throw error;
			window.location.reload();
			return new Promise((_, reject) => {
				// If navigation is prevented, surface the error instead
				// of leaving Suspense pending forever
				setTimeout(() => reject(error), 5000);
			});
		}
	} catch {
		// storage unavailable (private mode): fall through to rethrow
	}
	throw error;
};

/**
 * React.lazy wrapper that retries failed chunk imports.
 *
 * When a dynamic import fails (e.g. transient network failure on mobile after
 * resuming from background), the browser caches the failure in its module map:
 * retrying the exact same URL fails instantly. Retrying with a cache-busting
 * query param creates a fresh module map entry and fetches the chunk again.
 *
 * If all retries fail, the error is rethrown (ErrorBoundary handles it).
 */
export function lazyWithRetry(factory) {
	return lazy(() =>
		factory().then(
			(module) => {
				clearReloads();
				return module;
			},
			async (error) => {
				const url = error?.message?.match(
					/https?:\/\/[^\s'"]+\.(?:m?js|jsx)(?:[?#][^\s'"]*)?/,
				)?.[0];
				if (!url) {
					console.warn("lazyWithRetry: no chunk URL recovered from error", error);
					return coldReload(error);
				}

				const retryUrl = new URL(url, window.location.origin);
				if (retryUrl.origin !== window.location.origin) throw error;

				for (const ms of [1000, 2000]) {
					await sleep(ms);
					try {
						// Distinct URL per attempt: browsers cache failed imports in the module map
						retryUrl.searchParams.set("retry", String(Date.now()));
						const module = await import(/* @vite-ignore */ retryUrl.href);
						clearReloads();
						return module;
					} catch {
						// keep retrying
					}
				}
				// Cache-busts can't help a stale deploy (the hashed chunk 404s
				// for good): a cold reload fetches fresh index.html instead
				return coldReload(error);
			},
		),
	);
}
